import React from 'react';
import PropTypes from 'prop-types';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { formatCurrency, formatNumber, formatPercent, formatRatio, formatDateOnly } from '../../../shared/formattingUtils';
import './MetaAdsDetails.css';

const TOOLTIP_CONTENT_STYLE = {
  background: 'var(--sl-bg-primary)',
  border: '1px solid var(--sl-border-secondary)',
  borderRadius: 8,
  fontSize: 12,
};

const TOOLTIP_LABEL_STYLE = { color: 'var(--sl-text-secondary)', marginBottom: 4 };

const Kpi = ({ label, value }) => (
  <div className="mad-kpi">
    <div className="mad-kpi__label">{label}</div>
    <div className="mad-kpi__value">{value}</div>
  </div>
);
Kpi.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

const Fact = ({ label, value }) => (
  <div className="mad-fact"><span>{label}</span><b>{value == null ? '—' : value}</b></div>
);
Fact.propTypes = { label: PropTypes.string.isRequired, value: PropTypes.node };

const MetaAdsDetails = (props) => {
  const { mode, ad, campaign, kpis, attribution, daily_trend: dailyTrend,
          matched_events: matchedEvents, ads, marketing_insights_path: marketingInsightsPath } = props;
  const adSet = campaign && campaign.ad_set;
  const title = mode === 'ad' ? (ad && ad.name) : (campaign && campaign.name);
  const status = mode === 'ad' ? (ad && ad.status) : (campaign && campaign.status);
  const runs = campaign && campaign.start_time
    ? `${formatDateOnly(campaign.start_time)} – ${campaign.stop_time ? formatDateOnly(campaign.stop_time) : 'Ongoing'}`
    : null;

  return (
    <div className="mad-root">
      <a className="mad-back setlist-text-sm setlist-font-semibold figma-text tertiary" href={marketingInsightsPath}>
        ← Marketing Insights
      </a>

      <header className="mad-header">
        <h1 className="mad-title">{title}</h1>
        {status && <span className="mad-pill">{status}</span>}
        {mode === 'ad' && <div className="mad-sub">Campaign: {campaign && campaign.name}</div>}
      </header>

      <div className="mad-body">
        <aside className="mad-sidebar">
          {mode === 'ad' && (
            <div className="mad-card">
              <div className="mad-card__title">Creative</div>
              {ad && ad.creative_image_url
                ? <img className="mad-creative" src={ad.creative_image_url} alt={(ad && ad.creative_title) || 'Ad creative'} />
                : <div className="mad-creative mad-creative--empty">No creative image</div>}
              {ad && ad.creative_title && <div className="mad-creative__title">{ad.creative_title}</div>}
              {ad && ad.creative_body && <div className="mad-creative__body">{ad.creative_body}</div>}
            </div>
          )}

          <div className="mad-card">
            <div className="mad-card__title">Campaign &amp; ad set</div>
            <Fact label="Objective" value={campaign && campaign.objective} />
            <Fact label="Budget" value={formatCurrency(campaign && campaign.budget)} />
            <Fact label="Runs" value={runs} />
            {adSet && (
              <>
                <Fact label="Ad set goal" value={adSet.optimization_goal} />
                <Fact label="Ad set budget" value={formatCurrency(adSet.budget)} />
              </>
            )}
          </div>
        </aside>

        <section className="mad-main">
          <div className="mad-kpis">
            <Kpi label={mode === 'ad' ? 'Spend (ad lifetime)' : 'Spend (campaign lifetime)'} value={formatCurrency(kpis && kpis.spend)} />
            <Kpi label="Impressions" value={formatNumber(kpis && kpis.impressions)} />
            <Kpi label="Clicks" value={formatNumber(kpis && kpis.clicks)} />
            <Kpi label="CTR" value={formatPercent(kpis && kpis.ctr)} />
            <Kpi label="Reach" value={formatNumber(kpis && kpis.reach)} />
            <Kpi label="Purchases (Meta)" value={formatNumber(kpis && kpis.purchases)} />
            <Kpi label="Registrations (Meta)" value={formatNumber(kpis && kpis.registrations)} />
            <Kpi label="CPC" value={formatCurrency(kpis && kpis.cpc)} />
            <Kpi label="CPM" value={formatCurrency(kpis && kpis.cpm)} />
            <Kpi label="CPA" value={formatCurrency(kpis && kpis.cpa)} />
          </div>

          <div className="mad-card">
            <div className="mad-card__title">This event (Opendate-attributed)</div>
            <div className="mad-kpis mad-kpis--attr">
              <Kpi label="Tickets" value={formatNumber(attribution && attribution.tickets)} />
              <Kpi label="Revenue" value={formatCurrency(attribution && attribution.revenue)} />
              <Kpi label="Event ROAS" value={formatRatio(attribution && attribution.event_roas)} />
            </div>
            <p className="mad-note">Revenue/tickets are scoped to this event; Meta spend is the {mode === 'ad' ? 'ad' : 'campaign'}&apos;s lifetime total.</p>
          </div>

          {dailyTrend && dailyTrend.length > 0 && (
            <div className="mad-card">
              <div className="mad-card__title">Daily performance</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={dailyTrend} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={formatDateOnly} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} />
                  <Line type="monotone" dataKey="spend" stroke="#1570ef" strokeWidth={2} dot={false} name="Spend" />
                  <Line type="monotone" dataKey="purchases" stroke="#067647" strokeWidth={2} dot={false} name="Purchases" />
                  <Line type="monotone" dataKey="registrations" stroke="#6A4C93" strokeWidth={2} dot={false} name="Registrations" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>

      {mode === 'ad' && matchedEvents && matchedEvents.length > 0 && (
        <div className="mad-card">
          <div className="mad-card__title">Matched events</div>
          {matchedEvents.map((e) => (
            <a key={e.id} className="mad-list-row" href={e.path}>{e.name}</a>
          ))}
        </div>
      )}

      {mode === 'campaign' && ads && ads.length > 0 && (
        <div className="mad-card">
          <div className="mad-card__title">Ads in this campaign</div>
          <table className="mad-table">
            <thead>
              <tr>
                <th>Ad</th>
                <th className="num">Spend</th>
                <th className="num">Impressions</th>
                <th className="num">Clicks</th>
                <th className="num">Revenue (Opendate)</th>
              </tr>
            </thead>
            <tbody>
              {ads.map((a) => (
                <tr key={a.external_id}>
                  <td><a className="mad-table__link" href={a.path}>{a.name}</a></td>
                  <td className="num">{formatCurrency(a.spend)}</td>
                  <td className="num">{formatNumber(a.impressions)}</td>
                  <td className="num">{formatNumber(a.clicks)}</td>
                  <td className="num">{formatCurrency(a.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

MetaAdsDetails.propTypes = {
  mode: PropTypes.oneOf(['ad', 'campaign']).isRequired,
  ad: PropTypes.object,
  campaign: PropTypes.object,
  kpis: PropTypes.object,
  attribution: PropTypes.object,
  daily_trend: PropTypes.array,
  matched_events: PropTypes.array,
  ads: PropTypes.array,
  marketing_insights_path: PropTypes.string.isRequired
};

export default MetaAdsDetails;
